import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { submitExerciseAnswers } from "../../../api/exercises/questionsService";
import Slider from "rc-slider";
import "rc-slider/assets/index.css"; // base styles
import "../../../assets/css/custom-slider.css"; // custom styles

// Define question types
type QuestionType = "CHECKBOX" | "RADIO" | "TEXT" | "SLIDER";

interface QuestionOption {
  value: string;
  selected: boolean;
}

interface Question {
  id: string;
  content: string;
  type: QuestionType;
  required: boolean;
  options: (string | number)[];
  languageCode: string;
  formattedOptions?: QuestionOption[];
  answer?: string | string[];
  systemFeedbackType?: string;
  displayValues?: Record<string, string>;
}

const FeedbackPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const exerciseData = location.state;
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const questionsPerPage = 5;

  useEffect(() => {
    setIsLoading(true);

    const currentLang = i18n.language || "en";

    if (exerciseData && exerciseData.questions) {
      const processedQuestions = exerciseData.questions
        .flat() // Flatten the array in case it's nested
        .filter((q: Question) => q.languageCode === currentLang)
        .map((q: Question) => {
          let formattedQuestion = { ...q };
          // Set displayValues, min/max for special sliders
          if (q.type === "SLIDER") {
            if (q.systemFeedbackType === "BORG_SCALE") {
              formattedQuestion.options = [6, 20];
              formattedQuestion.displayValues = {
                "6": "No exertion at all",
                "7": "Extremely light",
                "8": "Very light",
                "9": "Very light to fairly light",
                "10": "Fairly light",
                "11": "Fairly light to somewhat hard",
                "12": "Somewhat hard",
                "13": "Somewhat hard",
                "14": "Hard",
                "15": "Hard (heavy)",
                "16": "Very hard",
                "17": "Very hard",
                "18": "Very, very hard",
                "19": "Extremely hard",
                "20": "Maximal exertion",
              };
              formattedQuestion.answer = "13";
            } else if (q.systemFeedbackType === "DIFFICULTY_SCALE") {
              formattedQuestion.options = [0, 10];
              formattedQuestion.displayValues = {
                "0": "Not difficult at all",
                "1": "Very easy",
                "2": "Easy",
                "3": "Mildly challenging/Light",
                "4": "Moderately easy",
                "5": "Somewhat difficult",
                "6": "Moderately difficult",
                "7": "Difficult/Hard",
                "8": "Very difficult",
                "9": "Extremely difficult",
                "10": "Maximal effort (Exhaustion)",
              };
              formattedQuestion.answer = "7";
            } else if (q.systemFeedbackType === "PAIN_SCALE") {
              formattedQuestion.options = [0, 10];
              formattedQuestion.displayValues = {
                "0": "No pain",
                "1": "Mil",
                "2": "Mild",
                "3": "Mild",
                "4": "Moderate",
                "5": "Moderate",
                "6": "Moderate",
                "7": "Severe",
                "8": "Severe",
                "9": "Severe",
                "10": "Very severe",
              };
              formattedQuestion.answer = "10";
            } else {
              formattedQuestion.answer =
                q.options && q.options.length > 0 ? String(q.options[0]) : "0";
            }
          } else if (q.type === "CHECKBOX") {
            formattedQuestion.formattedOptions = q.options.map((opt) => ({
              value: String(opt),
              selected: false,
            }));
            formattedQuestion.answer = [];
          } else if (q.type === "RADIO") {
            formattedQuestion.answer = "";
          } else if (q.type === "TEXT") {
            formattedQuestion.answer = "";
          }
          return formattedQuestion;
        });

      setQuestions(processedQuestions);
      setErrors({});
    } else {
      const storedData = localStorage.getItem(`exercise_${id}`);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData && parsedData.questions) {
          const processedQuestions = parsedData.questions
            .flat()
            .filter((q: Question) => q.languageCode === currentLang)
            .map((q: Question) => {
              let formattedQuestion = { ...q };

              if (q.type === "CHECKBOX") {
                formattedQuestion.formattedOptions = q.options.map((opt) => ({
                  value: String(opt),
                  selected: false,
                }));
                formattedQuestion.answer = [];
              } else if (q.type === "RADIO") {
                formattedQuestion.answer = "";
              } else if (q.type === "TEXT") {
                formattedQuestion.answer = "";
              } else if (q.type === "SLIDER") {
                formattedQuestion.answer =
                  q.options && q.options.length > 0
                    ? String(q.options[0])
                    : "0";
              }

              return formattedQuestion;
            });

          setQuestions(processedQuestions);
        }
      }
    }
    setCurrentPage(1);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, [exerciseData, id, i18n.language]);

  const handleCheckboxChange = (questionId: string, optionValue: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          const updatedOptions = (q.formattedOptions || []).map((opt) => {
            if (opt.value === optionValue) {
              return { ...opt, selected: !opt.selected };
            }
            return opt;
          });

          const selectedValues = updatedOptions
            .filter((opt) => opt.selected)
            .map((opt) => opt.value);

          if (selectedValues.length > 0 && errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return {
            ...q,
            formattedOptions: updatedOptions,
            answer: selectedValues,
          };
        }
        return q;
      })
    );
  };

  const handleRadioChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          if (errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const handleTextChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          if (value && errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const handleSliderChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    questions.forEach((q) => {
      if (q.required) {
        if (
          q.type === "CHECKBOX" &&
          (!q.answer || (q.answer as string[]).length === 0)
        ) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (q.type === "RADIO" && !q.answer) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (
          q.type === "TEXT" &&
          (!q.answer || (q.answer as string).trim() === "")
        ) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (q.type === "SLIDER" && !q.answer) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = questions.slice(
    indexOfFirstQuestion,
    indexOfLastQuestion
  );

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (currentPage < totalPages) {
      goToNextPage();
      return;
    }

    if (!validateForm()) {
      toast.error(t("alert.error.pleaseAnswerAllRequiredQuestions"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formattedAnswers = questions.map((q) => {
        let answerValue: string[] = [];

        if (q.type === "CHECKBOX") {
          answerValue = Array.isArray(q.answer) ? q.answer : [];
        } else if (
          q.type === "RADIO" ||
          q.type === "TEXT" ||
          q.type === "SLIDER"
        ) {
          answerValue = q.answer ? [q.answer as string] : [];
        }
        return {
          questionId: q.id,
          answer: answerValue,
        };
      });
      try {
        const planId = exerciseData?.planId || exerciseData?.id || "";
        const videoUrl = exerciseData?.videoUrl || [];
        if (!planId) {
          toast.error(t("alert.error.missingPlanId"));
          return;
        }
        await submitExerciseAnswers(
          id as string,
          planId,
          formattedAnswers,
          videoUrl
        );
        toast.success(t("alert.success.answersSubmittedSuccessfully"));
        setTimeout(() => {
          navigate("/exercise-list");
        }, 1000);
      } catch (error) {
        toast.error(t("alert.error.failedToSubmitAnswers"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-7xl space-y-5 mt-1 pb-20 lg:pb-4">
        <div className="flex items-center mb-2">
          <button
            className="mr-3 text-primary-600 hover:bg-primary-50 rounded-full p-2 transition"
            onClick={() => navigate("/exercise-list")}
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {exerciseData?.name}
          </h2>
        </div>

        <div
          className="bg-white rounded-xl shadow-md p-6"
          style={{ boxShadow: "6px 8px 24px 4px #F6944D1F" }}
        >
          <div className="space-y-8">
            {isLoading ? (
              <div className="text-center text-gray-400 py-10">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-400 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                <p className="mt-2">{t("login.loading")}</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                {t("title.noQuestionsAvailable")}
              </div>
            ) : (
              (() => {
                // Group special slider questions
                const specialTypes = [
                  "BORG_SCALE",
                  "DIFFICULTY_SCALE",
                  "PAIN_SCALE",
                ];
                const specialQuestions = currentQuestions.filter(
                  (q) =>
                    q.type === "SLIDER" &&
                    specialTypes.includes(q.systemFeedbackType || "")
                );
                const otherQuestions = currentQuestions.filter(
                  (q) =>
                    !(
                      q.type === "SLIDER" &&
                      specialTypes.includes(q.systemFeedbackType || "")
                    )
                );

                return (
                  <>
                    {/* Render special sliders in a single row if there are 2 or more */}
                    {specialQuestions.length > 0 && (
                      <div>
                        <div className="w-full text-center font-bold text-gray-700 text-xl mb-4">
                          {t("title.borgScale")}
                        </div>
                        <div
                          className={
                            specialQuestions.length === 1
                              ? "flex justify-center py-4 w-full"
                              : specialQuestions.length === 2
                                ? "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 py-4 w-full justify-center"
                                : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 py-4 w-full"
                          }
                        >
                          {specialQuestions.map((question) => (
                            <div
                              key={question.id}
                              className="flex flex-col items-center w-full"
                            >
                              <div className="mb-4 text-center font-bold text-gray-700 text-base px-2">
                                {question.content}
                              </div>
                              <div className="flex flex-row items-center justify-center w-full">
                                <div
                                  className="flex flex-col items-center pr-2"
                                  style={{
                                    height: "460px",
                                    justifyContent: "space-between",
                                    minWidth: "120px",
                                  }}
                                >
                                  {question.displayValues &&
                                    Object.entries(question.displayValues).map(
                                      ([val]) => (
                                        <div
                                          key={val}
                                          className="flex flex-row items-center w-full "
                                          style={{ height: "32px" }}
                                        >
                                          <span
                                            className={
                                              parseInt(
                                                question.answer as string
                                              ) === parseInt(val)
                                                ? " text-white bg-orange-500 ml-2 rounded-full w-6 h-6 flex items-center justify-center"
                                                : "ml-2 text-gray-700"
                                            }
                                            style={{
                                              marginLeft: "12px",
                                              textAlign: "left",
                                            }}
                                          >
                                            {val}
                                          </span>
                                          <span
                                            style={{
                                              flex: 1,
                                              height: "2px",
                                              background: "#e5e7eb",
                                              marginLeft: "16px",
                                              marginRight: "0",
                                            }}
                                          ></span>
                                        </div>
                                      )
                                    )}
                                </div>
                                {/* Orange vertical slider column */}
                                <div
                                  className="relative flex flex-col items-center"
                                  style={{
                                    height: "460px",
                                    justifyContent: "center",
                                    minWidth: "80px",
                                  }}
                                >
                                  <div
                                    className="absolute left-1/2 top-0 -translate-x-1/2 w-5 h-full bg-orange-500 rounded-full"
                                    style={{ zIndex: 0 }}
                                  ></div>
                                  <Slider
                                    vertical
                                    reverse
                                    min={Number(question.options[0])}
                                    max={Number(question.options[1])}
                                    value={Number(question.answer)}
                                    onChange={(value) =>
                                      handleSliderChange(
                                        question.id,
                                        String(value)
                                      )
                                    }
                                    className="custom-slider-thumb"
                                    // You can use handleStyle, trackStyle, railStyle for further customization
                                  />
                                </div>
                                <div
                                  className="flex flex-col items-center pr-2"
                                  style={{
                                    height: "460px",
                                    justifyContent: "space-between",
                                    minWidth: "120px",
                                  }}
                                >
                                  {question.displayValues &&
                                    Object.entries(question.displayValues).map(
                                      ([val, label]) => (
                                        <div
                                          key={val}
                                          className="flex flex-row items-center w-full"
                                          style={{ height: "32px" }}
                                        >
                                          <span
                                            className={
                                              parseInt(
                                                question.answer as string
                                              ) === parseInt(val)
                                                ? "font-bold text-orange-600 ml-2"
                                                : "ml-2 text-gray-700"
                                            }
                                            style={{
                                              marginLeft: "12px",
                                              textAlign: "left",
                                            }}
                                          >
                                            {label}
                                          </span>
                                        </div>
                                      )
                                    )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Render other questions as usual */}
                    {otherQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className="border-b pb-6 last:border-b-0"
                      >
                        <div className="flex items-start mb-2">
                          <span className="bg-primary-100 text-primary-700 h-7 w-7 rounded-full flex items-center justify-center mr-3 font-semibold">
                            {indexOfFirstQuestion + index + 1}
                          </span>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-800">
                              {question.content}
                              {question.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </h3>
                          </div>
                        </div>

                        <div className="ml-10 space-y-2">
                          {question.type === "CHECKBOX" && (
                            <div className="space-y-2">
                              {(question.formattedOptions || []).map(
                                (option, optIdx) => (
                                  <div
                                    key={`${question.id}-${optIdx}`}
                                    className="flex items-center"
                                  >
                                    <input
                                      id={`${question.id}-${optIdx}`}
                                      type="checkbox"
                                      className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                      checked={option.selected}
                                      onChange={() =>
                                        handleCheckboxChange(
                                          question.id,
                                          option.value
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`${question.id}-${optIdx}`}
                                      className="ml-3 text-gray-700"
                                    >
                                      {option.value}
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                          )}

                          {question.type === "RADIO" && (
                            <div className="space-y-2">
                              {question.options.map((option, optIdx) => (
                                <div
                                  key={`${question.id}-${optIdx}`}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`${question.id}-${optIdx}`}
                                    type="radio"
                                    name={`question-${question.id}`}
                                    className="h-5 w-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                                    checked={question.answer === option}
                                    onChange={() =>
                                      handleRadioChange(
                                        question.id,
                                        String(option)
                                      )
                                    }
                                  />
                                  <label
                                    htmlFor={`${question.id}-${optIdx}`}
                                    className="ml-3 text-gray-700"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}

                          {question.type === "TEXT" && (
                            <div>
                              <textarea
                                id={`question-${question.id}`}
                                rows={4}
                                className={`w-full px-3 py-2 border ${
                                  errors[question.id]
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300`}
                                placeholder={t(
                                  "input.placeholder.enterYourAnswer"
                                )}
                                value={question.answer as string}
                                onChange={(e) =>
                                  handleTextChange(question.id, e.target.value)
                                }
                              />
                            </div>
                          )}

                          {question.type === "SLIDER" && (
                            <div>
                              <div className="relative mb-1">
                                <input
                                  type="range"
                                  min={Number(question.options[0])}
                                  max={Number(question.options[1])}
                                  value={question.answer as string}
                                  onChange={(e) =>
                                    handleSliderChange(
                                      question.id,
                                      e.target.value
                                    )
                                  }
                                  className="w-full accent-primary-500 mt-4"
                                />
                                <div className="absolute w-full flex justify-center -top-3">
                                  <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-md text-sm font-medium">
                                    {question.answer}
                                  </span>
                                </div>
                              </div>
                              <div className="flex justify-between text-xs mt-1 text-primary-500">
                                {question.options.map((option, index) => (
                                  <span key={index}>{option}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {errors[question.id] && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors[question.id]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()
            )}
          </div>

          {/* Pagination controls - only show when not loading */}
          {!isLoading && totalPages > 1 && (
            <div className="mt-2 flex justify-center items-center space-x-2">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`p-2 rounded-full ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-primary-600 hover:bg-primary-50"}`}
                aria-label="Previous page"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Numbered pagination */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => {
                      setCurrentPage(pageNumber);
                      window.scrollTo(0, 0);
                    }}
                    className={`w-8 h-8 flex items-center justify-center rounded-full ${
                      currentPage === pageNumber
                        ? "bg-primary-600 text-white"
                        : "text-gray-700 hover:bg-primary-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              )}

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-full ${currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : "text-primary-600 hover:bg-primary-50"}`}
                aria-label="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Only show action buttons when not loading */}
          {!isLoading && (
            <div className="mt-2 flex justify-center items-center w-full">
              <div className="flex w-full max-w-lg gap-4">
                <button
                  onClick={async () => {
                    try {
                      const planId =
                        exerciseData?.planId || exerciseData?.id || "";
                      const videoUrl = exerciseData?.videoUrl || [];
                      if (planId) {
                        await submitExerciseAnswers(
                          id as string,
                          planId,
                          [],
                          videoUrl
                        );
                        toast.success(
                          t("alert.success.answersSubmittedSuccessfully")
                        );
                      }
                      navigate("/exercise-list");
                    } catch (error) {
                      console.error("Error submitting on cancel:", error);
                      navigate("/exercise-list");
                    }
                  }}
                  className="w-full px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-primary-300"
                >
                  {t("button.skip")}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full px-6 py-2 border border-transparent rounded-md shadow-sm text-sm justify-center font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-primary-300 flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 border-1 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("login.loading")}
                    </>
                  ) : currentPage === totalPages ? (
                    t("button.done")
                  ) : (
                    t("button.next")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
